<template>
  <div class="historial">
    <div v-if="cargando" class="d-flex justify-center pa-8">
      <v-progress-circular indeterminate color="primary" />
    </div>
    <div v-else-if="error" class="text-center pa-8 grey--text text--darken-1">
      <v-icon size="40" color="error lighten-1">mdi-alert-circle-outline</v-icon>
      <p class="mt-2 mb-0">{{ error }}</p>
    </div>
    <div v-else-if="!entradas.length" class="text-center pa-8 grey--text">
      <v-icon size="48" color="grey lighten-2">mdi-history</v-icon>
      <p class="mt-2 mb-0">{{ idArmario ? "Todavía no hay cambios registrados en este armario." : "Todavía no hay cambios registrados." }}</p>
      <p class="text-caption mt-1 mb-0">Aquí aparecerá quién sube, borra o mueve cada cosa y cuándo.</p>
    </div>

    <ul v-else class="historial-lista">
      <li v-for="e in entradas" :key="e.id" class="historial-entrada">
        <span class="historial-icono" :style="{ backgroundColor: info(e).color }">
          <v-icon x-small color="white">{{ info(e).icono }}</v-icon>
        </span>
        <div class="historial-texto">
          <div>
            <strong>{{ e.usuario || "Alguien" }}</strong>
            {{ info(e).texto }}
            <span v-if="e.detalle" class="historial-detalle">· {{ e.detalle }}</span>
          </div>
          <div class="historial-fecha">
            <span :title="fechaCompleta(e.created_at)">{{ hace(e.created_at) }}</span>
            <template v-if="!idArmario && e.id_armario">
              ·
              <a
                v-if="nombreArmario(e.id_armario)"
                href="#"
                class="historial-armario"
                title="Ver en el árbol"
                @click.prevent="$emit('ver-armario', e.id_armario)"
              >{{ nombreArmario(e.id_armario) }}</a>
              <span v-else>armario que ya no existe</span>
            </template>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import axios from "axios";

const ACCIONES = {
  armario_creado: { texto: "creó el armario", icono: "mdi-plus", color: "#43A047" },
  armario_editado: { texto: "editó el armario", icono: "mdi-pencil", color: "#FB8C00" },
  armario_eliminado: { texto: "eliminó el armario", icono: "mdi-delete", color: "#E53935" },
  carpetas_cambiadas: { texto: "cambió las carpetas", icono: "mdi-folder-multiple-outline", color: "#F9A825" },
  adjunto_movido: { texto: "movió un documento de carpeta", icono: "mdi-folder-move-outline", color: "#F9A825" },
  nota_editada: { texto: "editó la nota", icono: "mdi-note-text-outline", color: "#FFA000" },
  armario_movido: { texto: "movió el armario", icono: "mdi-file-tree-outline", color: "#5E35B1" },
  categoria_asignada: { texto: "cambió la categoría", icono: "mdi-palette", color: "#00897B" },
  adjunto_subido: { texto: "subió un documento", icono: "mdi-paperclip", color: "#1E88E5" },
  adjunto_editado: { texto: "editó un documento", icono: "mdi-pencil", color: "#FB8C00" },
  adjunto_eliminado: { texto: "eliminó un documento", icono: "mdi-paperclip-off", color: "#E53935" },
  documento_subido: { texto: "subió un documento del cliente", icono: "mdi-folder-account", color: "#1E88E5" },
  documento_eliminado: { texto: "eliminó un documento del cliente", icono: "mdi-folder-remove", color: "#E53935" },
  colocado_en_plano: { texto: "colocó el armario en un plano", icono: "mdi-map-marker-plus", color: "#117F9C" },
  quitado_del_plano: { texto: "quitó el armario del plano", icono: "mdi-map-marker-remove", color: "#78909C" },
};

export default {
  name: "HistorialLista",
  props: {
    idCliente: { type: [Number, String], required: true },
    // si se indica, solo los cambios de ese armario
    idArmario: { type: [Number, String], default: null },
    // { id_armario: nombre } para mostrar el nombre de cada armario
    nombres: { type: Object, default: () => ({}) },
  },
  data() {
    return { entradas: [], cargando: false, error: "" };
  },
  watch: {
    idCliente: "cargar",
    idArmario: "cargar",
  },
  mounted() {
    this.cargar();
  },
  methods: {
    async cargar() {
      if (!this.idCliente) return;
      this.cargando = true;
      this.error = "";
      try {
        const { data } = await axios.get(
          `${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/clientes/${this.idCliente}/historial`,
          { params: { id_armario: this.idArmario ?? undefined, limite: this.idArmario ? 100 : 200 } }
        );
        this.entradas = data.historial || [];
      } catch (e) {
        console.error("Error al cargar el historial:", e);
        this.error = "No se pudo cargar el historial.";
      } finally {
        this.cargando = false;
      }
    },
    info(e) {
      return ACCIONES[e.accion] || { texto: e.accion, icono: "mdi-circle-small", color: "#90A4AE" };
    },
    nombreArmario(id) {
      return this.nombres[id] || null;
    },
    fechaCompleta(fecha) {
      return new Intl.DateTimeFormat("es-ES", { dateStyle: "full", timeStyle: "short", timeZone: "Europe/Madrid" }).format(new Date(fecha));
    },
    hace(fecha) {
      const segundos = Math.round((Date.now() - new Date(fecha).getTime()) / 1000);
      const rtf = new Intl.RelativeTimeFormat("es", { numeric: "auto" });
      if (segundos < 60) return "hace un momento";
      if (segundos < 3600) return rtf.format(-Math.floor(segundos / 60), "minute");
      if (segundos < 86400) return rtf.format(-Math.floor(segundos / 3600), "hour");
      if (segundos < 86400 * 7) return rtf.format(-Math.floor(segundos / 86400), "day");
      return new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Madrid" }).format(new Date(fecha));
    },
  },
};
</script>

<style scoped>
.historial-lista {
  list-style: none;
  padding: 8px 4px;
  margin: 0;
}
.historial-entrada {
  position: relative;
  display: flex;
  gap: 12px;
  padding: 8px 4px;
}
/* línea de tiempo entre los iconos */
.historial-entrada:not(:last-child)::before {
  content: "";
  position: absolute;
  left: 15px;
  top: 32px;
  bottom: -8px;
  width: 2px;
  background: #e3e8ec;
}
.historial-icono {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  margin-left: 2px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}
.historial-texto {
  font-size: 0.875rem;
  color: #37474f;
  min-width: 0;
  overflow-wrap: anywhere;
}
.historial-armario {
  font-weight: 600;
  font-size: 0.8rem;
  color: #117f9c;
  text-decoration: none;
}
.historial-armario:hover {
  text-decoration: underline;
}
.historial-detalle {
  color: #607d8b;
}
.historial-fecha {
  font-size: 0.75rem;
  color: #90a4ae;
}
</style>
